import { MARKET_FILTERS } from '@api';
import { WORKING_LIST_ID } from '@terminal/config/constants';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { appPaths } from '@terminal/routes/app-paths';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useExploreStore } from '@terminal/views/explore/hooks/use-explore-store/useExploreStore';
import { insightsAssetFilter } from '@terminal/views/explore/utils/filter-utils/filter-utils';
import { useScreenerActions } from '@terminal/views/screener/use-screener-actions/useScreenerActions';
import { ASSET_FILTERS_KEY } from '@terminal/views/screener/use-screener-store/filter-utils';
import { useScreenerStore } from '@terminal/views/screener/use-screener-store/useScreenerStore';
import { SCENARIO_ASSET_FILTER_KEY } from '@terminal/widgets/scenario/hooks/use-scenario-store/filter-utils';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Actions } from './components/Modals';

export const useLinkingToolsNavigation = () => {
  const { t } = useTranslation('common');
  const [activeModal, setActiveModal] = useState<Actions | null>(null);
  const navigate = useNavigate();
  const { trackScenarioOpen, trackScreenerOpen } = usePosthogEvents();

  const { createNewDraft } = useScreenerActions();
  const setHasCustomDefaultAssetFilter = useExploreStore(
    state => state.setHasCustomDefaultAssetFilter
  );
  const clearScenarioPageEntityTags = useScenarioStore(
    state => state.clearScenarioPageEntityTags
  );
  const { setCustomDefaultTableTab, activeDraftFilterOptions = [] } =
    useScreenerStore(state => ({
      setCustomDefaultTableTab: state.setCustomDefaultTableTab,
      activeDraftFilterOptions:
        state.draftScreenFilterGroups?.[MARKET_FILTERS].activeFilterOptions,
    }));

  const { addFilterOption: addScenarioFilterOption } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });
  const { addFilterOption: addInsightsFilterOption } = useFilterActions({
    store: useExploreStore,
    group: 'ASSET_FILTERS',
  });

  const workingListFilterOption = {
    key: WORKING_LIST_ID,
    name: t('common:filters.workingList'),
    value: {
      workingList: 'working-list',
    },
  };

  const navigateToWatchlist = () => {
    setActiveModal(Actions.CreateWatchlist);
  };

  const navigateToScreener = (source: PosthogTrackingSource) => {
    setCustomDefaultTableTab('Performance');
    const activeDraftAssetFilterOptionKey = activeDraftFilterOptions.find(
      option => option.id === ASSET_FILTERS_KEY
    )?.options?.[0]?.key;

    trackScreenerOpen(source);
    createNewDraft({
      option: {
        filterKey: ASSET_FILTERS_KEY,
        option: workingListFilterOption,
      },
      group: MARKET_FILTERS,
      skipUnsavedCheck: activeDraftAssetFilterOptionKey === WORKING_LIST_ID,
    });
  };

  const navigateToScenario = (source: PosthogTrackingSource) => {
    clearScenarioPageEntityTags();
    addScenarioFilterOption({
      filterKey: SCENARIO_ASSET_FILTER_KEY,
      option: workingListFilterOption,
    });
    trackScenarioOpen(source);
    navigate(appPaths.scenario);
  };

  const navigateToInsights = () => {
    setHasCustomDefaultAssetFilter(true);
    addInsightsFilterOption({
      filterKey: insightsAssetFilter.key,
      option: workingListFilterOption,
    });
    navigate(appPaths.insights);
  };

  return {
    navigateToWatchlist,
    navigateToScreener,
    navigateToScenario,
    navigateToInsights,
    activeModal,
    setActiveModal,
  };
};
